import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    ActionIcon, Badge, Breadcrumbs, Anchor, Button, Group, Loader, NumberInput,
    Paper, SimpleGrid, Stack, Text, Textarea, TextInput, Title, Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconArrowLeft, IconArrowRight, IconCar, IconCurrencyDollar, IconHome2 } from '@tabler/icons-react';
import apiClient from './services/apiClient.js';

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatPlate = (plate) => {
    if (!plate || plate === 'unknown') return plate;
    if (plate.length === 6) return `${plate.substring(0, 3)}-${plate.substring(3)}`;
    return plate;
};

const formatCurrency = (value, locale = 'en-AU', currency = 'AUD') =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value || 0);

const toDateKey = (dateTimeUtc) => {
    if (!dateTimeUtc) return '';
    const d = new Date(dateTimeUtc);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatDateTime = (dateTimeUtc) => {
    if (!dateTimeUtc) return 'Unknown time';
    const locale = (navigator && navigator.language) || 'en-AU';
    return new Date(dateTimeUtc).toLocaleString(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
};

const getDateTimeInfo = (dateTimeUtc) => {
    const locale = (navigator && navigator.language) || "en";
    const dateTimeInfo = dateTimeUtc ? new Date(dateTimeUtc).toLocaleString(locale, {
        dateStyle: "long",
        timeStyle: "short"
    }) : null;
    return dateTimeInfo;
};

const formatDateOnly = (dateTimeUtc) => {
    if (!dateTimeUtc) return '-';
    const locale = (navigator && navigator.language) || 'en-AU';
    return new Date(dateTimeUtc).toLocaleDateString(locale, { dateStyle: 'medium' });
};

const formatTimeOnly = (dateTimeUtc) => {
    if (!dateTimeUtc) return '-';
    const locale = (navigator && navigator.language) || 'en-AU';
    return new Date(dateTimeUtc).toLocaleTimeString(locale, { timeStyle: 'short' });
};

const buildMonthRows = (year, month) => {
    const start = new Date(year, month, 1);
    const startDay = start.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < startDay; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
        cells.push(new Date(year, month, d));
    }
    while (cells.length < 42) cells.push(null);

    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
};

const VehicleDetailPage = () => {
    const { plate: plateParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const reduxVehicle = useSelector((state) => state.vehicle.selectedVehicle);
    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

    const routeDateTimeUtc = new URLSearchParams(location.search).get('dateTimeUtc');
    const initialView = location.state?.initialView || new URLSearchParams(location.search).get('view') || 'report';
    const _candidateSeed = location.state?.vehicle || reduxVehicle;
    const _candidatePlate = (_candidateSeed?.plate || '').replace(/-/g, '').toUpperCase();
    const _urlPlate = (plateParam || '').replace(/-/g, '').toUpperCase();
    const seedVehicle = _candidatePlate && _candidatePlate === _urlPlate ? _candidateSeed : null;
    const cleanPlate = _urlPlate || seedVehicle?.plate?.replace(/-/g, '').toUpperCase() || '';

    const [selectedVehicle, setSelectedVehicle] = useState(seedVehicle || null);
    const [vehicleDriveOffs, setVehicleDriveOffs] = useState([]);
    const [selectedVehicleHistory, setSelectedVehicleHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingNevdis, setIsCheckingNevdis] = useState(false);
    const [driveOffAmount, setDriveOffAmount] = useState('');
    const [reportPlate, setReportPlate] = useState(cleanPlate || '');
    const [reportedBy, setReportedBy] = useState(user?.name || '');
    const [reportNotes, setReportNotes] = useState('');
    const [reportPanelOpen, setReportPanelOpen] = useState(false);
    const [settlingRecordIds, setSettlingRecordIds] = useState(() => new Set());
    const [nevdisResult, setNevdisResult] = useState(null);
    const [activeMediaImage, setActiveMediaImage] = useState('');
    const thumbnailStripRef = useRef(null);

    const currentMonthStart = useMemo(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    }, []);
    const [calendarAnchorMonth, setCalendarAnchorMonth] = useState(currentMonthStart);
    const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

    const userRole = user?.['/roles']?.[0];
    const historyBatchSize = Number(process.env.REACT_APP_VEHICLE_HISTORY_BATCH_SIZE || 20);

    const getAccessToken = async () => getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE },
    });

    const fetchSelectedVehicle = async (plate, dateTimeUtc) => {
        if (!isAuthenticated || !userRole || !plate) return null;
        const accessToken = await getAccessToken();
        const params = {
            groupName: userRole,
            plate,
            dateTimeUtc: dateTimeUtc || routeDateTimeUtc || seedVehicle?.dateTimeUtc || new Date().toISOString(),
        };
        return apiClient.getSelectedVehicleForPlateAsync(params, accessToken);
    };

    const fetchDriveOffs = async (plate) => {
        if (!isAuthenticated || !userRole || !plate) return [];
        const accessToken = await getAccessToken();
        const params = { plate, groupName: userRole };
        return apiClient.getVehicleDriveOffsForPlateAsync(params, accessToken);
    };

    const fetchHistory = async (vehicle) => {
        if (!isAuthenticated || !userRole || !vehicle?.plate) return [];
        const accessToken = await getAccessToken();
        const allRows = [];
        let pageNo = 1;
        while (pageNo <= 12) {
            const params = {
                plate: vehicle.plate,
                groupName: userRole,
                pageNo,
                pageBatchSize: historyBatchSize,
                currentRecordId: vehicle.id,
            };
            const result = await apiClient.getVehicleHistoryAsync(params, accessToken);
            if (!result || result.length === 0) break;
            allRows.push(...result);
            if (result.length < historyBatchSize) break;
            pageNo += 1;
        }
        return allRows;
    };

    const refreshData = async () => {
        if (!isAuthenticated || !userRole || !cleanPlate || cleanPlate === 'UNKNOWN') return;
        setIsLoading(true);
        try {
            const vehicle = await fetchSelectedVehicle(cleanPlate, routeDateTimeUtc);
            if (vehicle) setSelectedVehicle(vehicle);
            const [driveOffs, history] = await Promise.all([
                fetchDriveOffs(cleanPlate),
                fetchHistory(vehicle || selectedVehicle || seedVehicle),
            ]);
            setVehicleDriveOffs(driveOffs || []);
            setSelectedVehicleHistory(history || []);
        } catch (err) {
            toast.error('Failed to load vehicle details.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!userRole) return;
        if (cleanPlate === 'UNKNOWN') return;
        if (!cleanPlate && !seedVehicle?.plate) return;
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, userRole, cleanPlate, routeDateTimeUtc]);

    useEffect(() => {
        setReportedBy(user?.name || '');
    }, [user?.name]);

    useEffect(() => {
        setReportPlate((selectedVehicle?.plate || cleanPlate || '').replace(/-/g, '').toUpperCase());
    }, [selectedVehicle?.plate, cleanPlate]);

    useEffect(() => {
        if (initialView !== 'history') return;
        const historySection = document.getElementById('vehicle-history-calendar');
        if (historySection) {
            setTimeout(() => historySection.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
        }
    }, [initialView]);

    useEffect(() => {
        if (!selectedVehicle?.plate) return;
        try {
            const raw = localStorage.getItem(`nevdis_cache_${selectedVehicle.plate}`);
            if (!raw) return;
            const cache = JSON.parse(raw);
            setNevdisResult({ ...cache, cached: true });
        } catch {
            // ignore cache parse errors
        }
    }, [selectedVehicle?.plate]);

    const handleCheckVicRoads = async () => {
        if (!selectedVehicle?.plate || selectedVehicle.plate === 'unknown') return;
        const plate = selectedVehicle.plate.replace(/-/g, '').toUpperCase();
        try {
            await navigator.clipboard.writeText(plate);
            toast.success(`Rego ${plate} copied — paste into VicRoads form`);
        } catch {
            // no-op
        }
        window.open(
            'https://www.vicroads.vic.gov.au/registration/buy-sell-or-transfer-a-vehicle/check-vehicle-registration/vehicle-registration-enquiry',
            '_blank'
        );
    };

    const handleCheckNevdis = async () => {
        if (!selectedVehicle || !isAuthenticated || !userRole) return;
        setIsCheckingNevdis(true);
        try {
            const accessToken = await getAccessToken();
            const params = {
                registration: selectedVehicle.plate,
                currentRecordId: selectedVehicle.id,
                groupName: userRole,
                registrationCheckTime: new Date().toLocaleString(),
            };
            const result = await apiClient.getVehicleRegistrationStatusManualCheckAsync(params, accessToken);
            const mapped = {
                plate: selectedVehicle.plate,
                vehicleMake: result.vehicleMake,
                vehicleModel: result.vehicleModel,
                vehicleColor: result.vehicleColour,
                isVehicleStolen: result.isVehicleStolen,
                lookupStatus: result.lookupStatus,
                message: result.message,
                checkedAt: new Date().toISOString(),
                cached: false,
            };
            setNevdisResult(mapped);
            localStorage.setItem(`nevdis_cache_${selectedVehicle.plate}`, JSON.stringify(mapped));
            setSelectedVehicle((prev) => ({
                ...prev,
                registeredMake: result.vehicleMake,
                registeredModel: result.vehicleModel,
                registeredColor: result.vehicleColour,
                isVehicleStolen: result.isVehicleStolen,
                message: result.message,
                lookupStatus: result.lookupStatus,
            }));
        } catch {
            toast.error('NEVDIS check failed. Please try again.');
        } finally {
            setIsCheckingNevdis(false);
        }
    };

    const handleReportDriveOff = async () => {
        if (!selectedVehicle || !driveOffAmount || !reportedBy || !isAuthenticated || !userRole) {
            toast.error('Please fill in Amount and Reported By.');
            return;
        }
        const amountNum = parseFloat(driveOffAmount);
        if (!isFinite(amountNum) || amountNum <= 0) {
            toast.error('Amount must be a positive number.');
            return;
        }

        setIsLoading(true);
        try {
            const accessToken = await getAccessToken();
            const normalizedPlate = (selectedVehicle.plate || '')
                .replace(/[^A-Z0-9]/gi, '')
                .toUpperCase();
            const params = {
                groupName: userRole,
                currentRecordId: selectedVehicle.id,
                driveOffAmount,
                reportedBy,
                plate: normalizedPlate,
                notes: reportNotes?.trim() || undefined,
            };
            const result = await apiClient.setVehicleDriveOffAsync(params, accessToken);

            setDriveOffAmount('');
            setReportNotes('');
            setReportPanelOpen(false);
            await refreshData();

            toast.success('Drive-off report submitted.');
        } catch (err) {
            console.error('Failed to submit drive-off report', err);
            toast.error('Failed to submit report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDiscardReport = () => {
        setDriveOffAmount('');
        setReportNotes('');
        setReportPlate((selectedVehicle?.plate || cleanPlate || '').replace(/[^A-Z0-9]/gi, '').toUpperCase());
        setReportPanelOpen(false);
    };

    const actuallySettleDriveOff = async (driveOffRecord, settleKey) => {
        if (!isAuthenticated || !userRole) {
            console.warn('Skipping mark-as-paid: user is unauthenticated or missing roles');
            return;
        }
        if (!driveOffRecord) {
            console.warn('Skipping mark-as-paid: missing drive-off record');
            return;
        }

        setSettlingRecordIds((prev) => {
            const next = new Set(prev);
            next.add(settleKey);
            return next;
        });

        try {
            const accessToken = await getAccessToken();
            const driveOffSettledDate = getDateTimeInfo(new Date());
            const params = {
                groupName: userRole,
                driveOffSettledDate,
            };

            await apiClient.postSettleVehicleDriveOffsAsync([driveOffRecord], accessToken, params);
            toast.success('Drive-off marked as paid');
            await refreshData();
        } catch (error) {
            console.error('Failed to settle drive-off record:', error);
            toast.error('Failed to mark as paid — please try again');
        } finally {
            setSettlingRecordIds((prev) => {
                const next = new Set(prev);
                next.delete(settleKey);
                return next;
            });
        }
    };

    const handleMarkDriveOffPaid = (driveOffRecord, settleKey) => {
        if (!driveOffRecord) return;

        modals.openConfirmModal({
            title: 'Mark drive-off as paid?',
            children: (
                <Text size="sm">
                    {`Settle the ${formatCurrency(driveOffRecord?.driveOffAmount || 0)} drive-off from ${formatDateTime(driveOffRecord?.dateTimeUtc)} for ${formatPlate(selectedVehicle?.plate || cleanPlate || 'UNKNOWN')}? This cannot be undone.`}
                </Text>
            ),
            labels: { confirm: 'Yes, mark as paid', cancel: 'Cancel' },
            confirmProps: { color: 'brand' },
            onConfirm: () => {
                actuallySettleDriveOff(driveOffRecord, settleKey);
            },
        });
    };

    const historyByDate = useMemo(() => {
        const map = {};
        const seen = new Set();
        const allItems = [...(selectedVehicleHistory || [])];
        // The history API excludes the current detection record — add it back so the calendar shows today's visit.
        if (selectedVehicle?.dateTimeUtc && selectedVehicle?.id) {
            allItems.push(selectedVehicle);
        }
        allItems.forEach((item) => {
            if (!item?.id || seen.has(item.id)) return;
            seen.add(item.id);
            const key = toDateKey(item.dateTimeUtc);
            if (!key) return;
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        return map;
    }, [selectedVehicleHistory, selectedVehicle]);

    const maxDailyCount = useMemo(() => {
        const counts = Object.values(historyByDate).map((arr) => arr.length);
        return counts.length ? Math.max(...counts) : 0;
    }, [historyByDate]);

    const yearMonthRows = useMemo(
        () => Array.from({ length: 12 }, (_, offset) => {
            const monthDate = new Date(calendarAnchorMonth.getFullYear(), calendarAnchorMonth.getMonth() - offset, 1);
            return {
                key: `${monthDate.getFullYear()}-${monthDate.getMonth() + 1}`,
                monthDate,
                rows: buildMonthRows(monthDate.getFullYear(), monthDate.getMonth()),
            };
        }),
        [calendarAnchorMonth]
    );

    const selectedDateRows = selectedCalendarDate ? (historyByDate[selectedCalendarDate] || []) : [];
    const previousVisits = Number.isFinite(selectedVehicle?.historyCount)
        ? selectedVehicle.historyCount
        : (selectedVehicleHistory?.length || 0);
    const totalVisits = Math.max(1, previousVisits + 1);
    const visitBadgeByStatus = {
        Red: { backgroundColor: '#DC2626', label: 'High Risk' },
        Green: { backgroundColor: '#16A34A', label: 'Regular Customer' },
        Amber: { backgroundColor: '#f59342', label: 'Needs Attention' },
        Neutral: { backgroundColor: '#0F172A', label: 'Unknown Pattern' },
    };
    const visitBadge = visitBadgeByStatus[selectedVehicle?.alertStatus] || visitBadgeByStatus.Neutral;
    const registrationImages = useMemo(() => {
        const seen = new Set();
        const collected = [];
        const pushIfValid = (url) => {
            if (!url || typeof url !== 'string') return;
            const trimmed = url.trim();
            if (!trimmed || seen.has(trimmed)) return;
            seen.add(trimmed);
            collected.push(trimmed);
        };

        // Only use full-frame/high-quality captures in the strip.
        pushIfValid(selectedVehicle?.detectionPlateImageUrl);
        pushIfValid(selectedVehicle?.vehicleImageUrl);
        (selectedVehicleHistory || []).forEach((item) => {
            pushIfValid(item?.detectionPlateImageUrl);
            pushIfValid(item?.vehicleImageUrl);
        });

        return collected;
    }, [selectedVehicle?.detectionPlateImageUrl, selectedVehicle?.vehicleImageUrl, selectedVehicleHistory]);

    useEffect(() => {
        if (registrationImages.length === 0) {
            setActiveMediaImage('');
            return;
        }
        if (!registrationImages.includes(activeMediaImage)) {
            setActiveMediaImage(registrationImages[0]);
        }
    }, [registrationImages, activeMediaImage]);

    const activeImageIndex = useMemo(
        () => registrationImages.findIndex((img) => img === activeMediaImage),
        [registrationImages, activeMediaImage]
    );

    const goToPreviousImage = () => {
        if (registrationImages.length <= 1) return;
        const currentIndex = activeImageIndex >= 0 ? activeImageIndex : 0;
        const prevIndex = (currentIndex - 1 + registrationImages.length) % registrationImages.length;
        setActiveMediaImage(registrationImages[prevIndex]);
    };

    const goToNextImage = () => {
        if (registrationImages.length <= 1) return;
        const currentIndex = activeImageIndex >= 0 ? activeImageIndex : 0;
        const nextIndex = (currentIndex + 1) % registrationImages.length;
        setActiveMediaImage(registrationImages[nextIndex]);
    };

    useEffect(() => {
        if (registrationImages.length <= 1) return undefined;

        const onKeyDown = (event) => {
            const activeTag = document.activeElement?.tagName?.toLowerCase();
            const isTypingContext =
                activeTag === 'input' ||
                activeTag === 'textarea' ||
                activeTag === 'select' ||
                document.activeElement?.isContentEditable;
            if (isTypingContext) return;

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                goToPreviousImage();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                goToNextImage();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [registrationImages.length, goToPreviousImage, goToNextImage]);

    useEffect(() => {
        if (!thumbnailStripRef.current || activeImageIndex < 0) return;
        const activeThumb = thumbnailStripRef.current.querySelector(`[data-thumb-index="${activeImageIndex}"]`);
        if (!activeThumb) return;
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, [activeImageIndex]);

    const previousDriveOffRows = useMemo(() => (
        (vehicleDriveOffs || []).map((driveOff, idx) => ({
            id: driveOff?.id || driveOff?.currentRecordId || `${driveOff?.dateTimeUtc || 'unknown'}-${idx}`,
            settleKey: driveOff?.currentRecordId || driveOff?.id || `${driveOff?.dateTimeUtc || 'unknown'}-${idx}`,
            rawRecord: driveOff,
            date: formatDateOnly(driveOff?.dateTimeUtc),
            time: formatTimeOnly(driveOff?.dateTimeUtc),
            evidenceUrl: driveOff?.detectionPlateImageUrl || driveOff?.vehicleImageUrl || driveOff?.regoPlateImageUrl || '',
            amount: driveOff?.driveOffAmount || 0,
            reportedBy: driveOff?.reportedBy || driveOff?.reportedByName || '-',
            notes: driveOff?.notes || '-',
        }))
    ), [vehicleDriveOffs]);
    const displayPlate = formatPlate(selectedVehicle?.plate || cleanPlate || 'UNKNOWN');
    const normalizedDisplayPlate = (selectedVehicle?.plate || cleanPlate || 'UNKNOWN').toUpperCase();
    const visitCountForTone = Number.isFinite(selectedVehicle?.historyCount)
        ? selectedVehicle.historyCount
        : (selectedVehicleHistory?.length || 0);
    const hasPreviousDriveOff = (vehicleDriveOffs?.length || 0) > 0 || Boolean(selectedVehicle?.isActiveDriveOff);
    const isUnknownPlate = !normalizedDisplayPlate || normalizedDisplayPlate === 'UNKNOWN';
    const isRegularCustomer = !hasPreviousDriveOff && !isUnknownPlate && visitCountForTone >= 5;
    const regoTone = hasPreviousDriveOff || isUnknownPlate
        ? {
            background: '#FEE2E2',
            text: '#991B1B',
          }
        : isRegularCustomer
            ? {
                background: '#DCFCE7',
                text: '#166534',
              }
            : {
                background: '#E2E8F0',
                text: '#334155',
              };
    return (
        <Stack gap="xl" px="xl" pt="xl" pb="xl">
            <Group justify="space-between" align="flex-end" wrap="nowrap">
                <Stack gap={4}>
                    <Breadcrumbs separator="/" separatorMargin={6} styles={{ separator: { color: '#CBD5E1' } }}>
                        <Anchor
                            component="button"
                            onClick={() => navigate('/dashboard')}
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                        >
                            <IconHome2 size={14} stroke={1.75} color="#64748B" />
                        </Anchor>
                        <Text size="xs" c="gray.5" fw={500}>Registration Details</Text>
                    </Breadcrumbs>
                    <Group gap="xs" align="center">
                        <span
                            style={{
                                backgroundColor: regoTone.background,
                                color: regoTone.text,
                                borderRadius: '10px',
                                padding: '4px 12px',
                                fontSize: '22px',
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                lineHeight: 1.2,
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            {displayPlate}
                        </span>
                        <Title order={3} c="gray.8" style={{ letterSpacing: '-0.01em' }}>
                            details
                        </Title>
                    </Group>
                </Stack>
                <Button
                    variant={reportPanelOpen ? 'light' : 'filled'}
                    color={reportPanelOpen ? 'gray' : 'red'}
                    radius="md"
                    leftSection={<IconCar size={16} stroke={1.75} />}
                    onClick={() => {
                        if (reportPanelOpen) {
                            handleDiscardReport();
                            return;
                        }
                        setReportPanelOpen(true);
                    }}
                >
                    Report Drive Off
                </Button>
            </Group>

            {previousDriveOffRows.length > 0 && (
                <Paper shadow="xs" radius="md" p={0} style={{ overflow: 'hidden', backgroundColor: '#FFF5F5' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ backgroundColor: '#FEE2E2' }}>
                                <tr>
                                    {['Date & Time', 'Evidence', 'Amount', 'Reported By', 'Notes', 'Action'].map((col) => (
                                        <th key={col} style={{
                                            fontSize: '12px', fontWeight: 600, color: '#64748B',
                                            textTransform: 'uppercase', letterSpacing: '0.04em',
                                            padding: '12px 16px', borderBottom: '1px solid #E2E8F0',
                                            whiteSpace: 'nowrap', textAlign: 'left',
                                        }}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {previousDriveOffRows.map((row, idx) => {
                                    const isLast = idx === previousDriveOffRows.length - 1;
                                    const tdStyle = {
                                        fontSize: '14px', color: '#334155',
                                        padding: '0 16px', height: '64px',
                                        borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
                                        verticalAlign: 'middle', textAlign: 'left',
                                    };
                                    return (
                                        <tr
                                            key={row.id}
                                            style={{ backgroundColor: '#FFF5F5' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFF5F5'}
                                        >
                                            <td style={tdStyle}>
                                                <Text size="sm" c="gray.7">{row.date}, {row.time}</Text>
                                            </td>
                                            <td style={tdStyle}>
                                                {row.evidenceUrl ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setActiveMediaImage(row.evidenceUrl)}
                                                        style={{
                                                            width: '44px', height: '44px', borderRadius: '8px',
                                                            overflow: 'hidden', border: '1px solid #E2E8F0',
                                                            padding: 0, background: '#fff', cursor: 'pointer',
                                                        }}
                                                    >
                                                        <img
                                                            src={row.evidenceUrl}
                                                            alt="Drive-off evidence"
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                        />
                                                    </button>
                                                ) : (
                                                    <div style={{
                                                        width: '44px', height: '44px', borderRadius: '8px',
                                                        backgroundColor: '#F1F5F9', border: '1px solid #E2E8F0',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <IconCar size={18} stroke={1.5} color="#94A3B8" />
                                                    </div>
                                                )}
                                            </td>
                                            <td style={tdStyle}>
                                                <Text size="sm" fw={600} c="gray.8">{formatCurrency(row.amount)}</Text>
                                            </td>
                                            <td style={tdStyle}>
                                                <Text size="sm" c="gray.7">{row.reportedBy}</Text>
                                            </td>
                                            <td style={{ ...tdStyle, whiteSpace: 'normal', maxWidth: '240px' }}>
                                                <Text size="sm" c="gray.7">{row.notes}</Text>
                                            </td>
                                            <td style={tdStyle}>
                                                <Group gap="xs" wrap="nowrap">
                                                    <Tooltip label="Mark as paid" withArrow>
                                                        <ActionIcon
                                                            variant="filled"
                                                            color="green"
                                                            size="sm"
                                                            radius="md"
                                                            loading={settlingRecordIds.has(row.settleKey)}
                                                            onClick={() => handleMarkDriveOffPaid(row.rawRecord, row.settleKey)}
                                                        >
                                                            <IconCurrencyDollar size={15} stroke={1.75} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                </Group>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Paper>
            )}

            {reportPanelOpen && (
                <Paper shadow="xs" radius="md" p="xl">
                    <Stack gap="lg">
                        <Title order={4} c="gray.8" style={{ textAlign: 'left', alignSelf: 'flex-start' }}>Report Drive Off</Title>
                        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                            <TextInput
                                label="Registration"
                                value={formatPlate(reportPlate)}
                                readOnly
                                radius="md"
                                size="sm"
                                styles={{ label: { fontSize: '13px', fontWeight: 500, color: '#334155', marginBottom: '6px', textAlign: 'left', width: '100%' } }}
                            />
                            <NumberInput
                                label="Amount ($)"
                                min={0}
                                step={0.01}
                                decimalScale={2}
                                value={driveOffAmount}
                                onChange={(val) => setDriveOffAmount(String(val ?? ''))}
                                placeholder="Enter amount"
                                radius="md"
                                size="sm"
                                styles={{ label: { fontSize: '13px', fontWeight: 500, color: '#334155', marginBottom: '6px', textAlign: 'left', width: '100%' } }}
                            />
                            <TextInput
                                label="Reported By"
                                value={reportedBy}
                                onChange={(e) => setReportedBy(e.target.value)}
                                radius="md"
                                size="sm"
                                styles={{ label: { fontSize: '13px', fontWeight: 500, color: '#334155', marginBottom: '6px', textAlign: 'left', width: '100%' } }}
                            />
                        </SimpleGrid>
                        <Textarea
                            label="Notes"
                            rows={3}
                            value={reportNotes}
                            onChange={(e) => setReportNotes(e.target.value)}
                            placeholder="Add context (e.g. customer returned to pay, accidental drive-off, staff observations)"
                            radius="md"
                            size="sm"
                            styles={{ label: { fontSize: '13px', fontWeight: 500, color: '#334155', marginBottom: '6px', textAlign: 'left', width: '100%' } }}
                        />
                        <Group justify="flex-end" gap="sm">
                            <Button
                                variant="subtle"
                                color="gray"
                                radius="md"
                                onClick={handleDiscardReport}
                                disabled={isLoading}
                            >
                                Discard
                            </Button>
                            <Button
                                variant="filled"
                                color="red"
                                radius="md"
                                onClick={handleReportDriveOff}
                                disabled={isLoading || !driveOffAmount || !reportedBy}
                                loading={isLoading}
                            >
                                Report
                            </Button>
                        </Group>
                    </Stack>
                </Paper>
            )}

            {registrationImages.length > 0 && (
                <Paper shadow="xs" radius="md" p="lg">
                    <Group justify="space-between" align="center" mb="md">
                        <Title order={4} c="gray.8">Captured Images</Title>
                        <Text size="xs" c="gray.5">
                            {registrationImages.length} image{registrationImages.length !== 1 ? 's' : ''} for this registration
                        </Text>
                    </Group>
                    <div
                        style={{
                            background: '#0f172a',
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            overflow: 'hidden',
                            marginBottom: '8px',
                            position: 'relative',
                        }}
                    >
                        <img
                            src={activeMediaImage}
                            alt={`Captured vehicle ${selectedVehicle?.plate || ''}`}
                            style={{
                                width: '100%',
                                height: 'min(52vh, 520px)',
                                objectFit: 'contain',
                                objectPosition: 'center',
                                display: 'block',
                            }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                padding: '8px 12px',
                                borderRadius: '10px',
                                color: '#fff',
                                backgroundColor: visitBadge.backgroundColor,
                                boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                                fontWeight: 700,
                                minWidth: '172px',
                            }}
                        >
                            <div style={{ fontSize: '12px', lineHeight: 1.1, opacity: 0.9 }}>{visitBadge.label}</div>
                            <div style={{ fontSize: '16px', lineHeight: 1.15 }}>
                                {totalVisits} visit{totalVisits !== 1 ? 's' : ''}
                            </div>
                        </div>
                        {registrationImages.length > 1 && (
                            <>
                                <ActionIcon
                                    variant="default"
                                    radius="md"
                                    size="lg"
                                    onClick={goToPreviousImage}
                                    aria-label="Previous image"
                                    style={{ position: 'absolute', top: '50%', left: '8px', transform: 'translateY(-50%)', opacity: 0.85 }}
                                >
                                    <IconArrowLeft size={16} stroke={1.75} />
                                </ActionIcon>
                                <ActionIcon
                                    variant="default"
                                    radius="md"
                                    size="lg"
                                    onClick={goToNextImage}
                                    aria-label="Next image"
                                    style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)', opacity: 0.85 }}
                                >
                                    <IconArrowRight size={16} stroke={1.75} />
                                </ActionIcon>
                            </>
                        )}
                    </div>
                    <Group ref={thumbnailStripRef} gap="xs" wrap="nowrap" style={{ overflowX: 'auto', paddingBottom: '4px' }}>
                        {registrationImages.map((imgUrl, idx) => (
                            <button
                                key={`${imgUrl}-${idx}`}
                                type="button"
                                data-thumb-index={idx}
                                onClick={() => setActiveMediaImage(imgUrl)}
                                style={{
                                    minWidth: '88px',
                                    height: '62px',
                                    overflow: 'hidden',
                                    borderRadius: '8px',
                                    border: activeMediaImage === imgUrl ? '3px solid #26A5DF' : '1px solid #ced4da',
                                    boxShadow: activeMediaImage === imgUrl ? '0 0 0 3px rgba(38,165,223,0.28)' : 'none',
                                    transform: activeMediaImage === imgUrl ? 'scale(1.02)' : 'none',
                                    transition: 'all 0.16s ease',
                                    padding: 0,
                                    background: '#fff',
                                }}
                            >
                                <img
                                    src={imgUrl}
                                    alt={`Thumbnail ${idx + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                />
                            </button>
                        ))}
                    </Group>
                </Paper>
            )}

            <Paper shadow="xs" radius="md" p="xl">
                <Stack gap="lg">
                    <Group gap="sm">
                        <Tooltip label="Ctrl+V to paste rego" withArrow position="top">
                            <Button variant="default" radius="md" onClick={handleCheckVicRoads}>
                                Check VicRoads
                            </Button>
                        </Tooltip>
                        <Tooltip label="$0.50 per search" withArrow position="top">
                            <Button variant="filled" color="brand" radius="md" onClick={handleCheckNevdis} loading={isCheckingNevdis}>
                                {isCheckingNevdis ? 'Checking…' : 'Check NEVDIS'}
                            </Button>
                        </Tooltip>
                    </Group>

                    {nevdisResult && (
                        <Paper withBorder radius="md" p="lg" style={{ borderColor: '#E2E8F0' }}>
                            <style>{`
                                @keyframes stolen-pulse {
                                    0%, 100% { background-color: #EF4444; box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
                                    50%       { background-color: #DC2626; box-shadow: 0 0 0 8px rgba(239,68,68,0); }
                                }
                            `}</style>

                            {/* Header row */}
                            <Group justify="space-between" align="flex-start" mb={4}>
                                <Group gap="sm" align="center">
                                    <Title order={5} c="gray.8">NEVDIS Result</Title>
                                    {nevdisResult.lookupStatus?.toUpperCase() === 'FOUND'
                                        ? <Badge radius="xl" color="green" variant="light" style={{ textTransform: 'none', fontWeight: 600 }}>Result Found</Badge>
                                        : <Badge radius="xl" color="red" variant="light" style={{ textTransform: 'none', fontWeight: 600 }}>Not Found</Badge>
                                    }
                                    {nevdisResult.lookupStatus?.toUpperCase() === 'FOUND' && (
                                        nevdisResult.isVehicleStolen ? (
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '3px 10px',
                                                borderRadius: '999px',
                                                color: '#fff',
                                                fontWeight: 700,
                                                fontSize: '12px',
                                                letterSpacing: '0.02em',
                                                animation: 'stolen-pulse 1s ease-in-out infinite',
                                            }}>
                                                ⚠ STOLEN VEHICLE
                                            </div>
                                        ) : (
                                            <Badge radius="xl" color="green" variant="light" size="sm" style={{ fontWeight: 600, textTransform: 'none' }}>
                                                ✓ Not Stolen
                                            </Badge>
                                        )
                                    )}
                                </Group>
                                {nevdisResult.cached && (
                                    <Badge radius="xl" color="yellow" variant="light">Cached</Badge>
                                )}
                            </Group>

                            {/* Subtitle: message immediately below heading */}
                            {nevdisResult.message && (
                                <Text size="xs" c="gray.5" mb="md" ta="left">{nevdisResult.message}</Text>
                            )}

                            {/* Single-row data line with pipe dividers */}
                            <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: '12px' }}>
                                <Group gap={0} wrap="nowrap" align="center">
                                    {[
                                        { label: 'Plate', value: formatPlate(nevdisResult.plate) },
                                        { label: 'Make', value: nevdisResult.vehicleMake || 'N/A' },
                                        { label: 'Model', value: nevdisResult.vehicleModel || 'N/A' },
                                        { label: 'Colour', value: nevdisResult.vehicleColor || 'N/A' },
                                    ].map(({ label, value }, idx) => (
                                        <Group key={label} gap={0} align="center" wrap="nowrap">
                                            {idx > 0 && (
                                                <div style={{ width: '1px', height: '18px', backgroundColor: '#CBD5E1', margin: '0 16px' }} />
                                            )}
                                            <Text size="sm" c="gray.7">
                                                <Text span fw={600} c="gray.8">{label}: </Text>
                                                {value}
                                            </Text>
                                        </Group>
                                    ))}
                                </Group>
                            </div>
                        </Paper>
                    )}
                </Stack>
            </Paper>

            <Paper id="vehicle-history-calendar" shadow="xs" radius="md" p="lg">
                <Stack gap="sm">
                    <Group justify="space-between" align="center">
                        <Title order={4} c="gray.8">Visit History (This Site)</Title>
                        <Group gap="xs">
                            <ActionIcon
                                variant="default"
                                radius="md"
                                onClick={() => setCalendarAnchorMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 12, 1))}
                            >
                                <IconArrowLeft size={14} stroke={1.75} />
                            </ActionIcon>
                            <Text size="sm" fw={600} c="gray.7">
                                {yearMonthRows[0]?.monthDate.toLocaleString('default', { month: 'short', year: 'numeric' })}
                                {' '}to{' '}
                                {yearMonthRows[yearMonthRows.length - 1]?.monthDate.toLocaleString('default', { month: 'short', year: 'numeric' })}
                            </Text>
                            <ActionIcon
                                variant="default"
                                radius="md"
                                disabled={calendarAnchorMonth >= currentMonthStart}
                                onClick={() => setCalendarAnchorMonth((prev) => {
                                    const next = new Date(prev.getFullYear(), prev.getMonth() + 12, 1);
                                    return next > currentMonthStart ? currentMonthStart : next;
                                })}
                            >
                                <IconArrowRight size={14} stroke={1.75} />
                            </ActionIcon>
                        </Group>
                    </Group>

                    <SimpleGrid cols={{ base: 2, md: 4, xl: 6 }} spacing={6}>
                        {yearMonthRows.map(({ key, monthDate, rows }) => (
                            <div key={`month-${key}`} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '6px' }}>
                                <Text style={{ fontSize: '10px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>
                                    {monthDate.toLocaleString('default', { month: 'short', year: 'numeric' })}
                                </Text>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                                    {rows.flat().map((day, idx) => {
                                        const dayKey = day ? toDateKey(day.toISOString()) : '';
                                        const count = dayKey ? (historyByDate[dayKey]?.length || 0) : 0;
                                        const intensity = maxDailyCount > 0 ? count / maxDailyCount : 0;
                                        const isSelected = selectedCalendarDate === dayKey;
                                        const todayKey = toDateKey(new Date().toISOString());
                                        const isToday = dayKey === todayKey;
                                        const isFuture = day && day > new Date();
                                        const bg = !day
                                            ? 'transparent'
                                            : isFuture
                                                ? '#F1F5F9'
                                                : count === 0
                                                    ? '#E2E8F0'
                                                    : `rgba(38,165,223,${0.25 + intensity * 0.75})`;
                                        const textColor = !isFuture && intensity > 0.45 ? '#ffffff' : '#64748B';
                                        const border = isToday
                                            ? '2px solid #F59E0B'
                                            : isSelected
                                                ? '2px solid #26A5DF'
                                                : 'none';

                                        return (
                                            <button
                                                key={`${monthDate.getFullYear()}-${monthDate.getMonth()}-${idx}`}
                                                type="button"
                                                disabled={!day}
                                                onClick={() => setSelectedCalendarDate(dayKey)}
                                                title={day ? `${day.toLocaleDateString()} · ${count} visit${count !== 1 ? 's' : ''}` : ''}
                                                style={{
                                                    width: '100%',
                                                    aspectRatio: '1 / 1',
                                                    borderRadius: '2px',
                                                    border,
                                                    backgroundColor: bg,
                                                    opacity: day ? 1 : 0,
                                                    cursor: day ? 'pointer' : 'default',
                                                    padding: 0,
                                                    fontSize: '8px',
                                                    fontWeight: isToday ? 700 : 500,
                                                    color: isFuture ? '#CBD5E1' : textColor,
                                                    lineHeight: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {day ? day.getDate() : ''}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </SimpleGrid>

                    <Text size="xs" c="gray.5">
                        Year view: each square is a day. Darker squares indicate higher visit counts.
                    </Text>

                    {selectedCalendarDate && (
                        <Stack gap="sm" pt="md" style={{ borderTop: '1px solid #F1F5F9' }}>
                            <Text fw={600} size="sm" c="gray.8">
                                {new Date(selectedCalendarDate).toLocaleDateString()} · {selectedDateRows.length} visit{selectedDateRows.length !== 1 ? 's' : ''}
                            </Text>
                            {selectedDateRows.length === 0 ? (
                                <Text size="sm" c="gray.5">No visits recorded.</Text>
                            ) : (
                                <Stack gap={4}>
                                    {selectedDateRows.map((row, idx) => (
                                        <Text key={`${selectedCalendarDate}-${idx}`} size="sm" c="gray.6">
                                            {new Date(row.dateTimeUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </Text>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Stack>
            </Paper>
        </Stack>
    );
};

export default VehicleDetailPage;
